import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { change } from 'redux-form';
import { withStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';

import IconButton from 'components/common/IconButton';
import Menu, { MenuItem } from 'components/common/Menu';
import DeleteMenu from 'components/common/DeleteMenu';
import IconMoreVert from 'components/icons/IconMoreVert';
import PrescriberCreateEditDialog from 'components/settings/PrescriberCreateEditDialog';
import PrescribersSearchDialog from 'components/settings/PrescribersSearchDialog';
import Tooltip from 'components/common/Tooltip';
import Link from 'components/common/Link';
import IconLink from 'components/icons/IconLink';

import splitLongWords from 'utils/splitLongWords';
import formatPrescriberName from 'utils/formatPrescriberName';

import { deletePrescriber } from 'redux/modules/prescribers';

import * as normalize from 'utils/normalize';

const styles = theme => ({
  iconMoreVert: {
    width: 24,
    height: 24,
  },
  buttonCell: {
    textAlign: 'right',
    padding: '0 10px 0 0',
    '&:last-child': {
      padding: '0 10px 0 0',
    },
  },
  typographyPecos: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: '50%',
    marginLeft: 8,
    color: theme.palette.common.white,
    cursor: 'pointer',
    backgroundColor: theme.palette.validation['500'],
  },
  typographyPecosEligible: {
    backgroundColor: theme.palette.add['500'],
  },
  typographyPecosInvalid: {
    backgroundColor: theme.palette.notification['500'],
  },
  cellPhone: {
    whiteSpace: 'nowrap',
  },
  linkCell: {
    padding: 0,
  },
  iconLink: {
    color: theme.palette.primary[500],
  },
  tooltipList: {
    textAlign: 'left',
  },
}); 

class PrescribersTableRow extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      deleteMenuOpen: false,
      editDialogOpen: false,
      searchDialogOpen: false,
    };

    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onDeleteMenuItemClick = this.onDeleteMenuItemClick.bind(this);
    this.onDeleteCancel = this.onDeleteCancel.bind(this);
    this.onDeleteSubmit = this.onDeleteSubmit.bind(this);
    this.onEditMenuItemClick = this.onEditMenuItemClick.bind(this);
    this.onEditDialogClose = this.onEditDialogClose.bind(this);
    this.onSearchDialogClose = this.onSearchDialogClose.bind(this);
    this.onPrescriberSelect = this.onPrescriberSelect.bind(this);
    this.onPrescriberSearchButtonClick = this.onPrescriberSearchButtonClick.bind(this);
  }

  onMenuButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      deleteMenuOpen: false,
    });
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onEditMenuItemClick() {
    this.setState({
      anchorEl: null,
      editDialogOpen: true,
    });
  }

  onDeleteMenuItemClick() {
    this.setState({ deleteMenuOpen: true });
  }

  onDeleteCancel() {
    this.setState({ anchorEl: null });
  }

  onDeleteSubmit() {
    const { ID, actions } = this.props;
    actions.deletePrescriber({ ID });
    this.setState({
      deleteMenuOpen: false,
      anchorEl: null,
    });
  }

  onEditDialogClose() {
    this.setState({ editDialogOpen: false });
  }

  onSearchDialogClose() {
    this.setState({ searchDialogOpen: false });
  }

  onPrescriberSelect(values) {
    const { ID, actions } = this.props;
    const newState = {
      searchDialogOpen: false,
      editDialogOpen: true,
    };
    if (values) {
      const form = `prescriberEdit-${ID}`;
      const primaryAddress = values.addresses[0];
      const primaryTaxonomy = values.taxonomies[0];
      let phone;
      if (primaryAddress.phone) {
        phone = primaryAddress.phone.indexOf('+1 ') !== -1 ? primaryAddress.phone.replace('+1 ', '') : primaryAddress.phone; 
      } else {
        const withPhoneAddress = values.addresses.filter(item => item.phone)[0];
        phone = withPhoneAddress ? withPhoneAddress.phone : null;
      }
      actions.change(form, 'First Name', values.first_name);
      actions.change(form, 'Last Name', values.last_name);
      actions.change(form, 'NPI', values.npi.toString());
      actions.change(form, 'LIC', primaryTaxonomy.license);
      if (values.name_prefix) {
        actions.change(form, 'Name Prefix', values.name_prefix);
      }
      if (values.name_suffix && values.name_suffix !== '--') {
        actions.change(form, 'Name Suffix', values.name_suffix);
      }
      if (values.credential) {
        actions.change(form, 'Credential', values.credential);
      }
      if (phone && phone.length > 0) {
        actions.change(form, 'Phone', normalize.phone(phone));
      }
      actions.change(form, 'address.Street', primaryAddress.address);
      actions.change(form, 'address.City', primaryAddress.city);
      actions.change(form, 'address.State', primaryAddress.state);
      actions.change(form, 'address.Zip Code', normalize.zip(primaryAddress.postal_code));
      actions.change(form, 'taxonomies', values.taxonomies.map(item => ({
        Code: item.code,
        Description: item.desc,
        'Is Primary': item.primary,
        ...item.state ? { State: item.state } : {},
        ...item.license ? { License: item.license } : {},
      })));
    }
    this.setState(newState);
  }

  onPrescriberSearchButtonClick() {
    this.setState({
      editDialogOpen: false,
      searchDialogOpen: true,
    });
  }

  render() {
    const {
      ID,
      Phone,
      Fax,
      Email,
      NPI,
      LIC,
      DEA,
      Taxonomies,
      account,
      classes,
    } = this.props;
    const {
      anchorEl,
      deleteMenuOpen,
      editDialogOpen,
      searchDialogOpen,
    } = this.state;
    const open = Boolean(anchorEl);

    return (
      <TableRow>
        <TableCell>
          <Link
            onClick={this.onEditMenuItemClick}
            breakWord
          >
            {splitLongWords(formatPrescriberName(this.props))}
            <Tooltip
              content={
                <Typography>
                  {this.props['Pecos Status'] === 'Active' && 'PECOS enrolled'}
                  {this.props['Pecos Status'] === 'Inactive' && 'PECOS not enrolled'}
                  {this.props['Pecos Status'] === 'Invalid' && 'Mistake in Prescriber information for PECOS'}
                </Typography>
              }
              placement="top"
            >
              <Typography
                className={classNames(classes.typographyPecos, {
                  [classes.typographyPecosEligible]: this.props['Pecos Status'] === 'Active',
                  [classes.typographyPecosInvalid]: this.props['Pecos Status'] === 'Invalid',
                })}
              >
                P
              </Typography>
            </Tooltip>
          </Link>
        </TableCell>
        <TableCell className={classes.cellPhone}>
          {Phone}
        </TableCell>
        {/* <TableCell className={classes.cellPhone}>
          {Fax}
        </TableCell> */}
        <TableCell>
          {Email
            && splitLongWords(Email)}
        </TableCell>
        <TableCell>
          {NPI}
        </TableCell>
        <TableCell>
          {LIC}
        </TableCell>
        <TableCell>
          {DEA}
        </TableCell>
        <TableCell>
          {(Taxonomies && Taxonomies[0]) && Taxonomies[0].Description}
        </TableCell>
        <TableCell className={classes.linkCell}>
          {(this.props['Referral Source'] && this.props['Referral Source'].length > 0) &&
            <Tooltip
              content={
                <div className={classes.tooltipList}>
                  <Typography variant="caption" gutterBottom>
                    Linked Referral Source:
                  </Typography>
                  {this.props['Referral Source'].map(item => (
                    <Typography key={item.ID}>
                      {item['Name of source']}
                    </Typography>
                  ))}
                </div>
              }
              placement="top"
            >
              <IconLink className={classes.iconLink} />
            </Tooltip>
          }
        </TableCell>
        <TableCell className={classes.buttonCell}>
          <IconButton
            aria-label="More"
            aria-owns={open ? `${ID}-prescriber-menu` : null}
            aria-haspopup="true"
            onClick={this.onMenuButtonClick}
            tooltipContent="Open Menu"
          >
            <IconMoreVert className={classes.iconMoreVert} />
          </IconButton>
          <Menu
            id={`${ID}-prescriber-menu`}
            anchorEl={anchorEl}
            open={open}
            onClose={this.onMenuClose}
            list={!deleteMenuOpen}
            maxHeight={130}
          >
            {deleteMenuOpen ? (
              <DeleteMenu
                text="Are you sure you want to delete this prescriber?"
                onCancel={this.onDeleteCancel}
                onSubmit={this.onDeleteSubmit}
              />
            ) : (
              <Fragment>
                <MenuItem
                  onClick={this.onEditMenuItemClick}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  onClick={this.onDeleteMenuItemClick}
                  color="warning"
                  disabled={account.Roles.filter(item =>
                    item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length === 0}
                >
                  Delete
                </MenuItem>
              </Fragment>
            )}
          </Menu>
        </TableCell>
        <PrescriberCreateEditDialog
          form={`prescriberEdit-${ID}`}
          open={editDialogOpen}
          ID={ID}
          initialValues={{
            'First Name': this.props['First Name'],
            'Last Name': this.props['Last Name'],
            Phone,
            ...this.props['Cell Phone'] ? { 'Cell Phone': this.props['Cell Phone'] } : {},
            ...Fax ? { Fax } : {},
            ...Email ? { Email } : {},
            NPI,
            ...LIC ? { LIC } : {},
            ...DEA ? { DEA } : {},
            ...this.props['Name Prefix'] ? { 'Name Prefix': this.props['Name Prefix'] } : {},
            ...this.props['Name Suffix'] ? { 'Name Suffix': this.props['Name Suffix'] } : {},
            ...this.props['Credential'] ? { Credential: this.props['Credential'] } : {},
            address: {
              ...this.props['Address Information'],
            },
            taxonomies: Taxonomies,
            'Referral Source': this.props['Referral Source'].map(item => ({
              value: item.ID,
              label: item['Name of source'],
            })),
          }}
          destroyOnUnmount={!searchDialogOpen}
          onClose={this.onEditDialogClose}
          onSearchButtonClick={this.onPrescriberSearchButtonClick}
        />
        <PrescribersSearchDialog
          form={`prescriberEditRegistrySearch-${ID}`}
          initialValues={{
            'First Name': this.props['First Name'],
            'Last Name': this.props['Last Name'],
            NPI,
          }}
          open={searchDialogOpen}
          clearOnOpen={false}
          onClose={this.onSearchDialogClose}
          onPrescriberSelect={this.onPrescriberSelect}
        />
      </TableRow>
    );
  }
}

PrescribersTableRow.propTypes = {
  ID: PropTypes.number.isRequired,
  'First Name': PropTypes.string.isRequired,
  'Last Name': PropTypes.string.isRequired,
  Phone: PropTypes.string,
  Fax: PropTypes.string,
  Email: PropTypes.string,
  NPI: PropTypes.string,
  LIC: PropTypes.string,
  DEA: PropTypes.string,
  'Pecos Check': PropTypes.bool,
  'Address Information': PropTypes.shape({
    Street: PropTypes.string,
  }),
  Taxonomies: PropTypes.arrayOf(PropTypes.shape({
    Description: PropTypes.string,
  })),
  actions: PropTypes.shape({
    deletePrescriber: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    iconMoreVert: PropTypes.string.isRequired,
    buttonCell: PropTypes.string.isRequired,
    typographyPecos: PropTypes.string.isRequired,
    typographyPecosEligible: PropTypes.string.isRequired,
    cellPhone: PropTypes.string.isRequired,
  }).isRequired,
};

PrescribersTableRow.defaultProps = {
  'Middle Name': null,
  Fax: null,
  Email: null,
  LIC: null,
  DEA: null,
  NPI: null,
  Phone: null,
  'Pecos Check': false,
  'Address Information': {},
  Taxonomies: [],
};

const mapStateToProps = store => ({
  account: store.account,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ deletePrescriber, change }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(PrescribersTableRow),
);



// WEBPACK FOOTER //
// ./src/components/settings/PrescribersTable/PrescribersTableRow.js